// Universal search in the header. Searches patient name, drug, or rx id across
// every state and shows a dropdown with the current status so you can see where
// a record is at before clicking in.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import type { SearchResult, RxStatus } from "@shared/types";

// Human-friendly status labels + where to go when clicked.
const STATUS_LABEL: Record<RxStatus, string> = {
  matched:          "In queue",
  tech_review:      "In queue",
  on_hold:          "On hold",
  ready_for_pk:     "Waiting for bot",
  pk_filing:        "Bot filing",
  imported_to_pk:   "Filed to PMS",
  pk_error_review:  "Bot error",
  rejected:         "Rejected",
  archived:         "Archived",
};
const STATUS_CLASS: Record<RxStatus, string> = {
  matched:          "bg-pill text-ink-soft",
  tech_review:      "bg-warn-soft text-warn",
  on_hold:          "bg-warn-soft text-warn",
  ready_for_pk:     "bg-pill text-ink-soft",
  pk_filing:        "bg-accent-soft text-accent",
  imported_to_pk:   "bg-good-soft text-good",
  pk_error_review:  "bg-bad-soft text-bad",
  rejected:         "bg-bad-soft text-bad",
  archived:         "bg-pill text-ink-soft",
};

export function UniversalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const ctrl = new AbortController();
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      apiGet<SearchResult[]>(`/search?q=${encodeURIComponent(q.trim())}`, ctrl.signal)
        .then((data) => { setResults(data); setOpen(true); })
        .catch(() => { if (!ctrl.signal.aborted) setResults([]); })
        .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    }, 200);
    return () => { ctrl.abort(); if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [q]);

  // Close on outside click.
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  function pick(r: SearchResult) {
    setOpen(false);
    setQ("");
    navigate(`/review/${r.rxId}`);
  }

  return (
    <div ref={containerRef} className="relative w-72">
      <input
        type="search"
        placeholder="Search patient, drug, or rx #…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        className="w-full px-3 py-1.5 rounded bg-white/95 text-sm placeholder-ink-soft focus:outline-none focus:ring-2 focus:ring-sky-400"
      />
      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-border max-h-96 overflow-auto z-20">
          {loading && results.length === 0 && (
            <p className="p-3 text-xs text-ink-soft">Searching…</p>
          )}
          {!loading && results.length === 0 && (
            <p className="p-3 text-xs text-ink-soft">No matches for "{q}".</p>
          )}
          {results.map((r) => (
            <button
              key={r.rxId}
              type="button"
              onClick={() => pick(r)}
              className="block w-full text-left px-3 py-2 hover:bg-pill border-b border-border last:border-b-0"
            >
              <div className="flex items-center gap-2 text-sm">
                <span className="text-ink-soft text-xs">#{r.rxId}</span>
                <strong className="truncate">{r.patientName}</strong>
                <span className="text-ink-soft text-xs">· {r.storeCode}</span>
                <span className={`ml-auto text-[11px] px-1.5 py-0.5 rounded ${STATUS_CLASS[r.status]}`}>
                  {STATUS_LABEL[r.status] ?? r.status}
                </span>
              </div>
              <div className="text-xs text-ink-soft truncate">{r.drug}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
